/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { store } from 'redux/store';

import SearchField from '@components/search-field';
import FilterHeader from '@components/filters/filter-header';
import SiteBookmark from './site-bookmark';
import SiteFilterGroup from '@components/filters/site-filter-group';
import TaskFilterGroup from '@components/filters/task-filter-group';

import useWindowDimensions from '@hooks/useWindowDimensions';
import { mapValues, countFilters, getSiteFilterDefaultValues } from './helper';
import { getAppDispatch } from '@utilities/dispatch.util';
import { checkValueInObjectIsNotEmpty } from '@utilities/helper.util';
import { filterActions } from '@redux/slices/filters';
import { useToastContext } from '@context/toaster.context';
import BookmarksService from '@services/bookmarks.service';
import { bookmarksActions } from '@redux/slices/bookmarks';

import ResetAndViewButton from '@components/filters/reset-view-button';
import DynamicFilterContainer from '@components/dynamic-filter-container';
import dynamicFilterList, { getCountOnEachFilterObject } from '@utilities/dynamic-filter-list';

import style from './style.module.scss';
import SaveModal from '@components/modal/variants/save-modal';
import { hasFilterChanged } from '@utilities/filter-is-changed';

const SiteFilter = ({
  length,
  filtersCount,
  setFilterActive,
  noGap,
  onSearch,
  resetFilters,
  setResetFilters,
  isFilterActive,
  setFiltersCount,
  onChangeFilters,
  sitesSiteTypeDataMap,
  sitesSiteStatusDataMap,
  sitesCheckPriorityDataMap,
  sitesAssignableUsersDataMap,
  sitesWorkOrderPriorityDataMap,
  sitesAreaHierarchyTreeDataMap,
  sites,
  selectedSort,
  bookmarkFilter,
  filterType,
  removeClassOnAssetDetailsFilters = false,
  siteId = null,
  detailPage = false,
  dynamicFilterCountOnEachField = 0,
  locationPermissionIsOn = false,
  equipmentsEquipmentCategoryDataMap = {},
  equipmentsEquipmentTypeDataMap = {}
}) => {
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const { ToasterUtil } = useToastContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const { width } = useWindowDimensions();
  const dueDateFilter = searchParams.get('dueDate');
  const downloadFilter = searchParams.get('download');
  const keyNameFilter = searchParams.get('keyName');
  const tabFilter = searchParams.get('tab');
  const bookmarkId = searchParams.get('bookmarkId');
  const bookmarkName = searchParams.get('bookmarkName');

  const isMapView = searchParams.get('showMapView') === 'true' ? true : false;
  const bookmarkSelectedSiteIdForMapView = searchParams.get('selectedSiteIdForMapView')
    ? JSON.parse(searchParams.get('selectedSiteIdForMapView'))
    : {};

  const { unit, coordinates } = useSelector((state) => state.cache);
  const sitesFilter = useSelector((state) => state?.filters?.sitesFilter) || {};
  const { showMapView = false, selectedSiteIdForMapView } =
    useSelector((state) => state.siteMap) || {};
  const {
    picklistsMap = {},
    areaHierarchyTreeDataMap = {},
    areaHierarchyDataMap
  } = useSelector((state) => state?.picklists);

  const [isActive, setActive] = useState(isFilterActive);
  const [inputValue, setInputValue] = useState('');
  const [filters, setFilters] = useState(getSiteFilterDefaultValues);
  const [initialY, setInitialY] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [updateBookmarkModalVisible, setUpdateBookmarkModalVisible] = useState(false);

  const dispatch = getAppDispatch();

  useEffect(() => {
    setActive(isFilterActive);
    if (isFilterActive) document.body.classList.add('filter-open');
    else document.body.classList.remove('filter-open');
  }, [isFilterActive]);

  useEffect(() => {
    return () => {
      if (isFilterActive) {
        setActive(false);
        document.body.classList.remove('filter-open');
      }
    };
  }, [isFilterActive]);

  const {
    siteTypeTags,
    siteStatusTags,
    assignableUsersTags,
    checkPriorityTags,
    workOrderPriorityTags,
    treeData,
    taskTypeTag,
    taskStatusTags,
    taskRecurringTemplateTags,
    workOrderTypeTags,
    workOrderTypeTagMap,
    inspectionTypeTags,
    inspectionRunTags,
    hseEventsCategoriesTags,
    checkRecurrancePriorityTags,
    checkTypeTags,
    checkTypeTagsMap,
    inspectionTypeTagsMap,
    equipmentCategoryTags,
    equipmentTypeTags
  } = useMemo(() => {
    return mapValues(
      sitesSiteTypeDataMap,
      sitesSiteStatusDataMap,
      sitesAssignableUsersDataMap,
      sitesCheckPriorityDataMap,
      sitesWorkOrderPriorityDataMap,
      sitesAreaHierarchyTreeDataMap,
      sites,
      { ...store.getState() },
      equipmentsEquipmentCategoryDataMap,
      equipmentsEquipmentTypeDataMap,
      filterType
    );
  }, [
    sitesSiteTypeDataMap,
    sitesSiteStatusDataMap,
    sitesAssignableUsersDataMap,
    sitesCheckPriorityDataMap,
    sitesWorkOrderPriorityDataMap,
    sitesAreaHierarchyTreeDataMap,
    filterType,
    equipmentsEquipmentCategoryDataMap,
    equipmentsEquipmentTypeDataMap
  ]);

  const startTouch = useCallback((e) => setInitialY(e.touches[0].clientY), []);

  const moveTouch = useCallback((e) => {
    let currentY = e.touches[0].clientY;
    let diffY = initialY - currentY;
    if (diffY < 0) setFilterActive(false);
    setInitialY(e.touches[0].clientY);
  }, []);

  useEffect(() => {
    if (!_.isEmpty(bookmarkFilter)) {
      const filtersInDecode = decodeURIComponent(bookmarkFilter);
      const filterInParse = JSON.parse(filtersInDecode);
      filterInParse?.filters && setFilters(filterInParse?.filters);
      filterInParse?.filters &&
        filterInParse?.filters['searched'] &&
        setInputValue(filterInParse?.filters['searched'] || '');
    } else if (checkValueInObjectIsNotEmpty(sitesFilter) && !detailPage) {
      //TODO
      setFilters(sitesFilter);
      onChangeFilters?.(sitesFilter);
      setInputValue(sitesFilter?.searched || '');
      onSearch(sitesFilter?.searched || '');
    } else {
      setInputValue('');
      setFilters(getSiteFilterDefaultValues);
    }
    if (resetFilters) resetBtnEvent({ filterActive: width < 768 ? false : true });
  }, [resetFilters, state?.resetFilters, bookmarkFilter]);

  const isFilterChangedInBookmark = useMemo(() => {
    if (!_.isEmpty(bookmarkFilter)) {
      const filtersInDecode = decodeURIComponent(bookmarkFilter);
      const oldFilter = JSON.parse(filtersInDecode);
      const oldFilterSearch = oldFilter?.filters['searched'] || '';

      const result =
        oldFilterSearch !== inputValue
          ? true
          : hasFilterChanged({
              oldFilter: oldFilter?.filters,
              newFilter: { ...filters }
            });

      if (result) {
        return result;
      } else {
        if (locationPermissionIsOn)
          if (
            showMapView !== isMapView ||
            (bookmarkSelectedSiteIdForMapView?.siteId || null) !==
              (selectedSiteIdForMapView?.siteId || null)
          ) {
            return true;
          } else {
            return false;
          }
      }
    } else {
      return false;
    }
  }, [
    bookmarkFilter,
    filters,
    inputValue,
    isMapView,
    bookmarkSelectedSiteIdForMapView,
    showMapView,
    selectedSiteIdForMapView
  ]);

  useEffect(() => {
    if (downloadFilter) updateFilters('download', 'selected', downloadFilter);
  }, [downloadFilter]);

  useEffect(() => {
    if (dueDateFilter === 'today') {
      updateFilters('dueDate', 'value', moment());
      updateFilters('dueDate', 'selected', 'on');
    } else if (dueDateFilter === 'past') {
      updateFilters('dueDate', 'selected', 'past');
      updateFilters('dueDate', 'value', ['Unlimited']);
    }
  }, [dueDateFilter]);

  useEffect(() => {
    const filterCount = countFilters(filters, inputValue, { ...store.getState() }) || 0;
    !detailPage && dispatch && dispatch?.(filterActions.setSitesFiltersCount(filterCount)); //TODO
    setFiltersCount(filterCount);
  }, [filters, inputValue, detailPage, dispatch]);

  const conditionClass = useMemo(() => {
    return width < 768 && removeClassOnAssetDetailsFilters && !isFilterActive
      ? style.displayNone
      : style.displayBlock;
  }, [isFilterActive]);

  const updateFilters = (name, key, value) => {
    const filtersCopy = { ...filters };
    let newFilter = {
      ...filtersCopy,
      [name]: {
        ...Object.assign({}, filtersCopy[name]),
        [key]: value,
        ...(key === 'selected' && value === 'on' && { value: '' }),
        ...(key === 'selected' && (value === 'past' || value === 'next') && { value: [] }),
        ...(key === 'selected' && value === 'range' && { value: ['', ''] }),
        ...(key === 'selected' && value === 'last' && { value: ['last'] })
      }
    };
    setFilters({ ...newFilter });
    onChangeFilters?.({ ...newFilter });
    !detailPage &&
      dispatch?.(filterActions.setSitesFilters({ ...newFilter, searched: inputValue })); //TODO
  };

  const resetBtnEvent = useCallback(({ filterActive = true }) => {
    setResetFilters(false);
    navigate(`${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`, {
      replace: true
    });
    setFilterActive(filterActive);
    setInputValue('');
    setFilters(getSiteFilterDefaultValues);
    onChangeFilters?.(getSiteFilterDefaultValues);
    onSearch('');
    !detailPage &&
      dispatch?.(filterActions.setSitesFilters({ ...getSiteFilterDefaultValues, searched: '' })); //TODO
  }, []);

  const leftBtnEvent = useCallback(() => setFilterActive(false));

  const handleUserInput = useCallback((e) => {
    const valueSearched = (e && e.target.value) || '';
    onSearch?.(valueSearched);
    !detailPage &&
      dispatch?.(filterActions.setSitesFilters({ ...filters, searched: valueSearched })); //TODO
    setInputValue(valueSearched);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (width < 768 && e.key === 'Enter') setFilterActive(false);
  }, []);

  const setUpdateBookmarkEvent = () => {
    const sort = !_.isEmpty(selectedSort)
      ? _.isArray
        ? { [selectedSort[0] === '# TASKS' ? 'Tasks' : selectedSort[0]]: true }
        : selectedSort
      : {};

    const filtersString = {
      filters: {
        ...filters,
        searched: inputValue,
        taskType: {
          value:
            filters?.taskType?.value?.length > 0
              ? filters?.taskType?.value
              : filterType === 'inspection' || filterType === 'Inspection'
              ? ['Inspections']
              : filterType === 'workOrder' || filterType === 'Work Order'
              ? ['Work orders']
              : filterType === 'workRequest'
              ? ['Work requests']
              : filterType === 'hseEvents'
              ? ['HSE event']
              : filterType === 'workRequest'
              ? ['Work requests']
              : filterType === 'Check' || filterType === 'check'
              ? ['Check']
              : filters?.taskType?.value || []
        }
      },
      sort,
      siteId
    };

    const siteBookmarkData = {
      filters: JSON.stringify(filtersString),
      showMapView,
      selectedSiteIdForMapView,
      bookmarkId
    };

    ToasterUtil.showSuccess(`Bookmark changes saved.`);
    BookmarksService.updateBookmarkData({ bookmarkId, updatedData: siteBookmarkData });
    dispatch?.(bookmarksActions.setBookmarkValidity(true));

    BookmarksService.updateBookmarksNameAndAtAGlance({
      name: 'Sites',
      atAGlance: true,
      bookmarkId,
      updatedData: siteBookmarkData
    });

    const params = new URLSearchParams(searchParams);
    params.set('bookmarkFilter', JSON.stringify(filtersString));
    setSearchParams(params, { replace: true });
    setUpdateBookmarkModalVisible(false);
  };

  const filterConfigs = useMemo(() => {
    const filterConfigs = dynamicFilterList({
      type: 'site-listing',
      updateFilters,
      areaHierarchyDataMap,
      filters,
      picklistsMap,
      taskStatusTags,
      taskTypeTag,
      assignableUsersTags,
      taskRecurringTemplateTags,
      workOrderTypeTagMap,
      inspectionRunTags,
      inspectionTypeTagsMap,
      checkTypeTagsMap,
      hseEventsCategoriesTags,
      equipmentCategoryTags,
      equipmentTypeTags
    });

    return filterConfigs;
  }, [
    areaHierarchyDataMap,
    filters,
    picklistsMap,
    taskStatusTags,
    taskTypeTag,
    assignableUsersTags,
    taskRecurringTemplateTags,
    workOrderTypeTagMap,
    inspectionRunTags,
    inspectionTypeTagsMap,
    checkTypeTagsMap,
    hseEventsCategoriesTags,
    equipmentCategoryTags,
    equipmentTypeTags
  ]);

  return (
    <>
      <div
        className={`op-sidebarfilter ${isActive && 'open'} ${!noGap && 'gap'} ${conditionClass} 
        ${isActive ? style.isActivePadding : style.isInActivePadding}
         `}
      >
        <div
          onTouchStart={startTouch}
          onTouchMove={moveTouch}
          className={`${isActive ? style.paddingLeftAndRight : style.isInActivePadding}`}
        >
          <FilterHeader
            title="Filters"
            filterCount={filtersCount || 0}
            resetBtnEvent={() => resetBtnEvent({})}
            leftBtnEvent={leftBtnEvent}
            openBookmarkModalEvent={() => {
              if (bookmarkId || isFilterChangedInBookmark) {
                if (isFilterChangedInBookmark) setUpdateBookmarkModalVisible(true);
              } else {
                setIsModalVisible(true);
              }
            }}
            bookmarkId={bookmarkId ? true : false}
            isFilterChangedInBookmark={isFilterChangedInBookmark}
            width={width}
            detailPage={detailPage}
          />
        </div>

        {/* start here */}

        <div
          className={`op-filter-search ${
            isActive ? style.paddingLeftAndRight : style.isInActivePadding
          } `}
          style={{ marginTop: '25px', marginBottom: '16px' }}
        >
          <SearchField
            placeholder="Search"
            value={inputValue}
            onKeyDown={handleKeyDown}
            onChange={handleUserInput}
            isClearable
          />

          <DynamicFilterContainer
            filterConfigs={filterConfigs}
            filters={filters}
            unit={unit}
            filterCount={dynamicFilterCountOnEachField || 0}
            resetBtnEvent={() => resetBtnEvent({})}
          />
        </div>

        <SiteFilterGroup
          coordinates={coordinates}
          filters={filters}
          unit={unit}
          updateFilters={updateFilters}
          isActive={isActive}
          treeData={treeData}
          areaHierarchyTreeDataMap={areaHierarchyTreeDataMap}
          siteTypeTags={siteTypeTags}
          picklistsMap={picklistsMap}
          siteStatusTags={siteStatusTags}
          siteListing
          filterType={filterType}
        />

        {!removeClassOnAssetDetailsFilters && (
          <TaskFilterGroup
            filterType={filterType === 'workRequest' ? 'Work Request' : filterType}
            taskTypeTag={taskTypeTag}
            filters={filters}
            updateFilters={updateFilters}
            taskStatusTags={taskStatusTags}
            workOrderTypeTags={workOrderTypeTags}
            workOrderTypeTagsMap={workOrderTypeTagMap}
            assignableUsersTags={assignableUsersTags}
            picklistsMap={picklistsMap}
            workOrderPriorityTags={workOrderPriorityTags}
            taskRecurringTemplateTags={taskRecurringTemplateTags}
            isActive={isActive}
            inspectionRunTags={inspectionRunTags}
            hseEventsCategoriesTags={hseEventsCategoriesTags}
            checkRecurrancePriorityTags={checkRecurrancePriorityTags}
            checkTypeTags={checkTypeTags}
            checkTypeTagsMap={checkTypeTagsMap}
            inspectionTypeTagsMap={inspectionTypeTagsMap}
            inspectionTypeTags={inspectionTypeTags}
            equipmentCategoryTags={equipmentCategoryTags}
            equipmentTypeTags={equipmentTypeTags}
          />
        )}

        <ResetAndViewButton
          clickResetButton={() => resetBtnEvent({ filterActive: false })}
          length={length}
          setFilterActiveEvent={(value) => setFilterActive(value)}
          setIsModalVisibleEvent={(value) => setIsModalVisible(value)}
          width={width}
          detailPage={detailPage}
          updateBookmarkId={bookmarkId}
          bookmarkName={bookmarkName}
          setUpdateBookmarkEvent={() => {
            setUpdateBookmarkModalVisible(true);
          }}
        />
      </div>
      {isModalVisible && (
        <SiteBookmark
          open={isModalVisible}
          setOpen={(value) => {
            setIsModalVisible(value);
          }}
          filters={{ ...filters, searched: inputValue }}
          selectedSort={selectedSort}
          parentSiteId={siteId}
          keyName={keyNameFilter}
          tab={tabFilter}
          filterType={filterType}
        />
      )}

      {updateBookmarkModalVisible && (
        <SaveModal
          open={updateBookmarkModalVisible}
          setOpen={(value) => {
            setUpdateBookmarkModalVisible(value);
          }}
          bookmarkSaveChanges={true}
          onSave={setUpdateBookmarkEvent}
        />
      )}
    </>
  );
};

export default React.memo(SiteFilter, _.isEqual);
